import DatabaseModel from './DatabaseModel.js';
import CacheService from './Cache.js';

/**
 * Adapter OEA — todas las llamadas al backend del dominio OEA
 * (Organización Equipo de Auditores / dashboard control cifras OEA).
 *
 * El adapter NO envía campos usuario / creadoPor / modificadoPor en
 * payloads. El backend usa SecurityUtils.getLoggedUsername() para
 * todos los campos de auditoría (decalogo-ruben.md §2).
 *
 * Ningún método incluye query string ?usuario=X — el filtrado por
 * usuario es responsabilidad del backend vía SecurityUtils.
 *
 * Patrón espejo de Oeb.js. URLs verificadas contra
 * OeaDashboardController.java (@RequestMapping("/oea")).
 */

var Oea = Object.create(DatabaseModel);

Oea.model = 'oea';

/**
 * Olvida lo cacheado del servicio OEA (60 s de sendRequest) después de una mutación. Sin esto la
 * recarga que viene detrás se serviría de la foto anterior bajo un sello que la anuncia como
 * fresca: enseñar lo de antes de guardar es justo lo que #16 viene a evitar.
 * Mismo patrón que Pedido.js / Tarifa.js.
 *
 * También se tira ante un corte sin respuesta, donde el servidor puede haber aplicado el cambio
 * igualmente: si no, la relectura con la que se comprueba qué quedó devolvería durante 60 s la
 * respuesta anterior con su sello de antes. El error se vuelve a lanzar tal cual: quien
 * llamó tiene que seguir enterándose de que la operación no se confirmó.
 */
function invalidarTrasMutacion(promesa) {
    return promesa.then(function(res) {
        CacheService.clearCache(Oea.model);
        return res;
    }).catch(function(err) {
        if (Oea.esResultadoIncierto(err)) CacheService.clearCache(Oea.model);
        throw err;
    });
}

// La petición se cortó sin respuesta (timeout de pasarela o status 0). DatabaseModel.sendRequest
// lo trata aparte porque en estos estados el servidor puede haber aplicado la operación igualmente:
// quien llame no puede dar el cambio por fallido ni deshacerlo a ciegas, tiene que releer.
// Gemelo de Oeb.esResultadoIncierto.
Oea.esResultadoIncierto = function(err) {
    if (!err) return false;
    return err.status === 0 || [408, 504, 520, 522, 524, 598, 599].indexOf(err.status) !== -1;
};

// ============================================================
// Ficha mensual
// ============================================================

/**
 * Devuelve la ficha mensual OEA (anio/mes).
 * @param {number} anio
 * @param {number} mes
 * @returns {Promise<Object>} ResponseDto<OeaFichaMensualMO>
 */
Oea.getFichaMensual = function(anio, mes) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + anio + '/' + mes,
        method: 'GET'
    }, true);
};

/**
 * Devuelve la ficha mensual OEA enriquecida con descuento de ausencias del
 * calendario (vacaciones T0006, bajas T0084, fiestas individuales T0083,
 * compensación T0085). Específico de la pantalla Control de cifras OEA.
 *
 * La ficha lleva `cifrasTpcCargadasEn` (#26): cuándo entraron por última vez las cifras TPC. Es la
 * fecha del sello de la pantalla, que por eso ya no dice "datos en vivo" — lo era la estructura de
 * la ficha, no las cifras. Es null en las fichas anteriores a #26, que no se rellenan hacia atrás.
 *
 * Sin la caché de peticiones (60 s), por lo mismo que Oeb.getFichaPlanificacion: la ficha la tocan
 * varias personas a la vez, así que la invalidación tras mutación solo cubre lo que se guarda en
 * este navegador. Cambiar de mes y volver dentro del minuto, o recargar, tiene que traer lo que
 * haya recalculado otro —y el sello que venga con ello— y no la foto anterior.
 *
 * @param {number} anio
 * @param {number} mes
 * Trae también `horasDiarias` (#40): mapa codigoPersonal → horas de trabajo al día, ya resueltas
 * contra la Configuración OEA (8 h para quien no las tenga puestas). Alimenta las tarjetas
 * Nº HORAS MES y PRODUCTIVIDAD VENTAS.
 *
 * @returns {Promise<Object>} ResponseDto<{ficha: OeaFichaMensualMO, ausencias: Map<String, List<{dia, motivo}>>, horasDiarias: Map<String, Number>}>
 */
Oea.getFichaControlCifras = function(anio, mes) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha-control-cifras/' + anio + '/' + mes,
        method: 'GET'
    }, false);
};

/**
 * Crea la ficha mensual OEA si no existe.
 * @param {number} anio
 * @param {number} mes
 * @returns {Promise<Object>} ResponseDto<OeaFichaMensualMO>
 */
Oea.crearFicha = function(anio, mes) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/crear',
        method: 'POST',
        data: { anio: anio, mes: mes }
    }, false));
};

/**
 * Recalcula objetivos de la ficha mensual con los objetivos anuales vigentes.
 * @param {number} anio
 * @param {number} mes
 * @returns {Promise<Object>} ResponseDto<OeaFichaMensualMO>
 */
Oea.recalcularFicha = function(anio, mes) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/recalcular-ficha/' + anio + '/' + mes,
        method: 'POST',
        data: {}
    }, false));
};

/**
 * Recalcula las cifras desde reparto. Si anio/mes nulos, actualiza la ficha activa.
 * @param {number} anio
 * @param {number} mes
 * @returns {Promise<Object>} ResponseDto<String>
 */
Oea.actualizarCifras = function(anio, mes) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/actualizar-cifras',
        method: 'POST',
        data: { anio: anio, mes: mes }
    }, false));
};

// ============================================================
// Personas (configuración)
// ============================================================

/**
 * Lista de personas activas en configuración OEA.
 * @returns {Promise<Object>} ResponseDto<List<OeaConfigPersonaMO>>
 */
Oea.getPersonasConfig = function() {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/personas',
        method: 'GET'
    }, true);
};

/**
 * Lista de personas detectadas desde reparto para el periodo dado.
 * @param {number} anio
 * @param {number} mes
 * @returns {Promise<Object>} ResponseDto<List<String>>
 */
Oea.getPersonasReparto = function(anio, mes) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/personas-reparto/' + anio + '/' + mes,
        method: 'GET'
    }, true);
};

/**
 * Crea una persona en configuración OEA.
 * @param {Object} datos OeaConfigPersonaMO
 * @returns {Promise<Object>} ResponseDto<OeaConfigPersonaMO>
 */
Oea.crearPersona = function(datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/personas',
        method: 'POST',
        data: datos
    }, false));
};

/**
 * Devuelve la ficha de configuración de una persona.
 * @param {string} codigo codigoPersonal
 * @returns {Promise<Object>} ResponseDto<OeaConfigPersonaMO>
 */
Oea.getPersona = function(codigo) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/personas/' + codigo,
        method: 'GET'
    }, true);
};

/**
 * Desactiva (soft-delete) una persona OEA.
 * @param {string} codigo codigoPersonal
 * @returns {Promise<Object>} ResponseDto<String>
 */
Oea.eliminarPersona = function(codigo) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/personas/' + codigo,
        method: 'DELETE'
    }, false));
};

// ============================================================
// Personas dentro de una ficha mensual
// ============================================================

/**
 * Agrega una persona a una ficha mensual existente.
 * @param {string} fichaId
 * @param {string} codigo codigoPersonal
 * @returns {Promise<Object>} ResponseDto<OeaFichaMensualMO>
 */
Oea.agregarPersonaAFicha = function(fichaId, codigo) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + fichaId + '/persona/' + codigo,
        method: 'POST',
        data: {}
    }, false));
};

/**
 * Quita una persona de la pantalla del mes. NO borra sus cifras (#34): deja de verse y, si se
 * la vuelve a añadir, sus datos del mes siguen donde estaban.
 * @param {string} fichaId
 * @param {string} codigo codigoPersonal
 * @returns {Promise<Object>} ResponseDto<OeaFichaMensualMO>
 */
Oea.quitarPersonaDeFicha = function(fichaId, codigo) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + fichaId + '/persona/' + codigo,
        method: 'DELETE'
    }, false));
};

/**
 * Fija para el mes qué personas se ven en Control de cifras OEA (#34). La lista es de la
 * aplicación, no de este usuario: a partir de aquí la ve cualquiera que abra la pantalla.
 * Solo ADMIN; el backend lo comprueba igualmente.
 * @param {string} fichaId
 * @param {Array<string>} codigos codigoPersonal de las personas que quedan visibles
 * @returns {Promise<Object>} ResponseDto<OeaFichaMensualMO>
 */
Oea.guardarPersonasVisibles = function(fichaId, codigos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + fichaId + '/personas-visibles',
        method: 'PUT',
        data: { codigos: codigos }
    }, false));
};

/**
 * Cambia el objetivo diario de una persona en su ficha mensual.
 * Backend usa SecurityUtils para registrar el modificador.
 * @param {string} fichaId
 * @param {string} codigo
 * @param {Object} datos { objetivoDiario }
 * @returns {Promise<Object>} ResponseDto<OeaFichaMensualMO>
 */
Oea.cambiarObjetivoDiarioEnFicha = function(fichaId, codigo, datos) {
    var self = this;
    var payload = { objetivoDiario: datos.objetivoDiario };
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + fichaId + '/persona/' + codigo + '/objetivo',
        method: 'PUT',
        data: payload
    }, false));
};

/**
 * Modifica los días hábiles de una semana de una persona en su ficha mensual.
 * Backend usa SecurityUtils para el modificador.
 * @param {string} fichaId
 * @param {string} codigo
 * @param {Object} datos { semanaNumero, diasHabiles, motivo }
 * @returns {Promise<Object>} ResponseDto<OeaFichaMensualMO>
 */
Oea.modificarDiasHabiles = function(fichaId, codigo, datos) {
    var self = this;
    var payload = {
        semanaNumero: datos.semanaNumero,
        diasHabiles: datos.diasHabiles,
        motivo: datos.motivo
    };
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + fichaId + '/persona/' + codigo + '/dias',
        method: 'PUT',
        data: payload
    }, false));
};

/**
 * Cambia la clasificación de una persona en su ficha mensual.
 * @param {string} fichaId
 * @param {string} codigo
 * @param {Object} datos { clasificacion }
 * @returns {Promise<Object>} ResponseDto<OeaFichaMensualMO>
 */
Oea.cambiarClasificacionEnFicha = function(fichaId, codigo, datos) {
    var self = this;
    var payload = { clasificacion: datos.clasificacion };
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + fichaId + '/persona/' + codigo + '/clasificacion',
        method: 'PUT',
        data: payload
    }, false));
};

// ============================================================
// Reportes por email
// ============================================================

/**
 * Envía el reporte OEA del periodo a las direcciones indicadas.
 * @param {Object} datos { anio, mes, emails, emailsCc, emailsCco, asunto, mensajeAdicional, tipoReporte }
 * @returns {Promise<Object>} ResponseDto<String>
 */
Oea.enviarReporte = function(datos) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/enviar-reporte',
        method: 'POST',
        data: datos
    }, false);
};

// ============================================================
// Objetivos anuales
// ============================================================

/**
 * Lista los objetivos anuales OEA del año dado.
 *
 * Sin la caché de peticiones (60 s): Configuración OEA lleva el sello "datos en vivo" (#16) y la
 * editan varios administradores, así que la invalidación tras mutación solo cubre lo que se guarda
 * en este navegador. Volver a la pantalla, o cambiar de año y regresar, dentro del minuto tiene que
 * traer lo que haya guardado otro y no la foto anterior.
 *
 * @param {number} anio
 * @returns {Promise<Object>} ResponseDto<List<OeaObjetivoAnualMO>>
 */
Oea.getObjetivosAnio = function(anio) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/objetivos/' + anio,
        method: 'GET'
    }, false);
};

/**
 * Guarda el objetivo anual de una persona.
 * @param {number} anio
 * @param {string} codigo
 * @param {Object} datos OeaObjetivoAnualMO
 * @returns {Promise<Object>} ResponseDto<OeaObjetivoAnualMO>
 */
Oea.guardarObjetivo = function(anio, codigo, datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/objetivos/' + anio + '/' + codigo,
        method: 'PUT',
        data: datos
    }, false));
};

/**
 * Aplica un factor multiplicativo a todos los objetivos del año.
 * @param {number} anio
 * @param {number} factor
 * @returns {Promise<Object>} ResponseDto<String>
 */
Oea.aplicarFactorObjetivos = function(anio, factor) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/objetivos/' + anio + '/aplicar-factor',
        method: 'POST',
        data: { factor: factor }
    }, false));
};

/**
 * Copia los objetivos de un año a otro aplicando un factor.
 * @param {Object} datos { anioOrigen, anioDestino, factor }
 * @returns {Promise<Object>} ResponseDto<String>
 */
Oea.copiarObjetivos = function(datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/objetivos/copiar',
        method: 'POST',
        data: datos
    }, false));
};

/**
 * Migra los objetivos de la antigua tabla de configuración al nuevo modelo OeaObjetivoAnualMO.
 * @returns {Promise<Object>} ResponseDto<String>
 */
Oea.migrarObjetivosDesdeConfig = function() {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/objetivos/migrar-desde-config',
        method: 'POST',
        data: {}
    }, false));
};

// ============================================================
// Resumen anual
// ============================================================

/**
 * Devuelve el resumen anual OEA del año dado.
 *
 * Cada entrada de `totalesMes` lleva `cifrasTpcCargadasEn` (#26): cuándo entraron por última vez
 * las cifras TPC en la ficha de ESE mes. Va por mes porque cada ficha se actualiza por su cuenta;
 * la pantalla condensa las doce en una sola fecha —la más antigua—. Es null en los meses sin ficha
 * y en las fichas anteriores a #26, que no se rellenan hacia atrás.
 *
 * Sin la caché de peticiones (60 s): su botón "Actualizar" no hace más que volver a pedir esto.
 * Cacheado, ese botón no traería nada nuevo durante el minuto siguiente, y volver al mismo año
 * enseñaría la foto anterior aunque otro haya tocado la ficha del mes. El resumen se recompone en
 * cada petición, así que la única forma de que el sello diga la verdad es preguntar siempre.
 *
 * @param {number} anio
 * @returns {Promise<Object>} ResponseDto<Map<String,Object>>
 */
Oea.getResumenAnual = function(anio) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/resumen-anual/' + anio,
        method: 'GET'
    }, false);
};

// ============================================================
// Instrucciones (transversal — InfoIcon de cada pantalla OEA)
// ============================================================

/**
 * Lista las instrucciones de una pantalla OEA.
 * @param {string} pantalla clave de pantalla (control-cifras, configuracion, resumen-anual, ejercicios, ...)
 * @returns {Promise<Object>} ResponseDto<List<OeaInstruccionMO>>
 */
Oea.getInstrucciones = function(pantalla) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/instrucciones/' + encodeURIComponent(pantalla),
        method: 'GET'
    }, true);
};

/**
 * Devuelve un único concepto por su clave estable. Útil para el componente
 * InfoIcon que carga conceptos puntuales por clave + pantalla.
 * @param {string} pantalla
 * @param {string} clave
 * @returns {Promise<Object>} ResponseDto<OeaInstruccionMO>
 */
Oea.getConcepto = function(pantalla, clave) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/concepto/' + encodeURIComponent(pantalla) + '/' + encodeURIComponent(clave),
        method: 'GET'
    }, true);
};

// [#164] Las cinco mutaciones de instrucciones invalidan como el resto del adapter. Quien las
// llama recarga la lista justo después —al inicializar el set por defecto, y en el `catch` del
// reordenado para deshacer el movimiento que no cuajó—, y esa recarga pasa por getInstrucciones,
// que sí está cacheado: sin tirar la caché, la relectura devuelve la foto de antes y el `catch`
// restaura una lista anterior a cambios que sí se guardaron. Gemelo del de Oeb.js.
/**
 * Inicializa el set por defecto de instrucciones para una pantalla.
 * @param {string} pantalla
 * @returns {Promise<Object>} ResponseDto<List<OeaInstruccionMO>>
 */
Oea.inicializarInstrucciones = function(pantalla) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/instrucciones/inicializar/' + encodeURIComponent(pantalla),
        method: 'POST',
        data: {}
    }, false));
};

/**
 * Crea una instrucción nueva en una pantalla.
 * @param {Object} datos OeaInstruccionMO ({ pantalla, titulo, texto, orden, ... })
 * @returns {Promise<Object>} ResponseDto<OeaInstruccionMO>
 */
Oea.crearInstruccion = function(datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/instrucciones',
        method: 'POST',
        data: datos
    }, false));
};

/**
 * Actualiza una instrucción.
 * @param {string} id
 * @param {Object} datos OeaInstruccionMO
 * @returns {Promise<Object>} ResponseDto<OeaInstruccionMO>
 */
Oea.actualizarInstruccion = function(id, datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/instrucciones/' + id,
        method: 'PUT',
        data: datos
    }, false));
};

/**
 * Elimina una instrucción.
 * @param {string} id
 * @returns {Promise<Object>} ResponseDto<String>
 */
Oea.eliminarInstruccion = function(id) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/instrucciones/' + id,
        method: 'DELETE'
    }, false));
};

/**
 * Reordena las instrucciones de una pantalla según el array de ids dado.
 * @param {string[]} ids
 * @returns {Promise<Object>} ResponseDto<String>
 */
Oea.reordenarInstrucciones = function(ids) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/instrucciones/orden',
        method: 'PUT',
        data: { ids: ids }
    }, false));
};

// ============================================================
// Ejercicios anuales
// ============================================================

/**
 * Lista los campos disponibles para filtrar ejercicios.
 * @returns {Promise<Object>} ResponseDto<List<String>>
 */
Oea.getEjerciciosCamposFiltro = function() {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ejercicios/campos-filtro',
        method: 'GET'
    }, true);
};

/**
 * Lista los valores disponibles para un campo de filtro de ejercicios.
 * @param {string} campo
 * @returns {Promise<Object>} ResponseDto<List<String>>
 */
Oea.getEjerciciosValoresFiltro = function(campo) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ejercicios/valores-filtro/' + encodeURIComponent(campo),
        method: 'GET'
    }, true);
};

/**
 * Todas las listas de valores de la ventana de Filtros en una sola petición (#43).
 *
 * Antes se pedía un campo por clic, y cada petición volvía a recorrer la colección de reparto
 * entera para agrupar: ocho recorridos en serie por pasar la ventana. Cliente no viene aquí,
 * se busca escribiendo con buscarEjerciciosClientes.
 *
 * @returns {Promise<Object>} ResponseDto<{valores, buscarEnServidor, calculadoEn}>
 */
Oea.getEjerciciosValoresFiltroTodos = function() {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ejercicios/valores-filtro',
        method: 'GET'
    }, true);
};

/**
 * Busca clientes por código o nombre para el filtro Cliente (#43). Hacen falta al menos dos
 * letras; con menos el servidor devuelve lista vacía.
 * @param {string} texto
 * @param {number} [limite]
 * @returns {Promise<Object>} ResponseDto<List<{codigo, nombre}>>
 */
Oea.buscarEjerciciosClientes = function(texto, limite) {
    var self = this;
    var url = self.server_movilidad + self.prefix + self.model
        + '/ejercicios/clientes?q=' + encodeURIComponent(texto || '');
    if (limite) url += '&limite=' + limite;
    return self.sendRequest({ url: url, method: 'GET' }, true);
};

/**
 * Nombre de unos códigos de cliente concretos (#43). Lo necesita la ventana al abrirse sobre una
 * consulta guardada: los códigos ya están elegidos y sin esto se verían pelados.
 * @param {string[]} codigos
 * @returns {Promise<Object>} ResponseDto<List<{codigo, nombre}>>
 */
Oea.getEjerciciosClientesPorCodigo = function(codigos) {
    var self = this;
    var lista = (codigos || []).map(function(c) { return encodeURIComponent(c); }).join(',');
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ejercicios/clientes?codigos=' + lista,
        method: 'GET'
    }, true);
};

/**
 * Genera/recalcula los datos de ejercicios para un rango de años con filtros.
 * @param {Object} datos { anioDesde, anioHasta, filtros, recalcular, soloAnioActual }
 * @returns {Promise<Object>} ResponseDto<Map<String,Object>>
 */
Oea.getEjercicios = function(datos) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ejercicios',
        method: 'POST',
        data: datos
    }, false);
};

// ============================================================
// Consultas guardadas
// ============================================================

/**
 * Lista las consultas guardadas. NO envía ?usuario= — backend filtra
 * por usuario logueado vía SecurityUtils (IDOR fix).
 * @returns {Promise<Object>} ResponseDto<List<OeaConsultaGuardadaMO>>
 */
Oea.getConsultas = function() {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/consultas',
        method: 'GET'
    }, true);
};

/**
 * Crea una consulta guardada. NO envía creadoPor en payload — backend
 * debe asignarlo vía SecurityUtils.getLoggedUsername().
 *
 * NOTA: deuda técnica abierta — actualmente el backend NO sobrescribe
 * creadoPor en crearConsulta (a diferencia de crearInstruccion).
 * Hasta que se mergee el fix backend, las consultas se guardan con
 * creadoPor=null. Ver C:\Users\JJCS\mongo-temp\DEUDA-TECNICA-OEA-OEB.md.
 *
 * @param {Object} datos OeaConsultaGuardadaMO ({ nombre, descripcion, filtros, compartida, usuarios, ... })
 * @returns {Promise<Object>} ResponseDto<OeaConsultaGuardadaMO>
 */
// [#164] Mismo olvido que las instrucciones, encontrado al barrer el adapter: Ejercicios recarga
// con getConsultas —cacheado— justo después de crear, renombrar, compartir o borrar una consulta.
Oea.crearConsulta = function(datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/consultas',
        method: 'POST',
        data: datos
    }, false));
};

/**
 * Actualiza una consulta guardada.
 * @param {string} id
 * @param {Object} datos OeaConsultaGuardadaMO
 * @returns {Promise<Object>} ResponseDto<OeaConsultaGuardadaMO>
 */
Oea.actualizarConsulta = function(id, datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/consultas/' + id,
        method: 'PUT',
        data: datos
    }, false));
};

/**
 * Elimina una consulta guardada.
 * @param {string} id
 * @returns {Promise<Object>} ResponseDto<String>
 */
Oea.eliminarConsulta = function(id) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/consultas/' + id,
        method: 'DELETE'
    }, false));
};

/**
 * Guarda el orden de las consultas según el array de ids dado.
 * @param {string[]} orden
 * @returns {Promise<Object>} ResponseDto<String>
 */
Oea.reordenarConsultas = function(orden) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/consultas/orden',
        method: 'PUT',
        data: { orden: orden }
    }, false));
};

export default Oea;
